/**
 * 设备素材库清单：`src/assets/models` 下 glb/png 与左侧「Mockup Type」分类对应关系。
 * 新增机型时在此追加一行并放入对应子目录即可（路径相对于 `src/assets/models/`）。
 */

/** 与编辑器左侧「Mockup Type」、Pinia `mockupCategory` 一致 */
export type MockupModelCategoryId = 'phone' | 'tablet' | 'laptop' | 'watch' | 'other'

/**
 * 单条库配置（不含构建期解析后的 URL，由 `mockupDevices` 内合并为 {@link MockupDeviceTemplate}）。
 */
export interface MockupModelLibraryEntry {
  /** 图层 `deviceTemplateId`、工程 JSON 中保存的稳定 id */
  id: string
  /** 与左侧分类下拉一致 */
  category: MockupModelCategoryId
  /** 展示名称 */
  name: string
  /** 屏幕逻辑分辨率（贴图/导出参考） */
  width: number
  height: number
  /**
   * 相对 `src/assets/models/` 的 glb 路径；留空字符串则 Three 视口使用占位几何体。
   * @example `Phone/iphone16.glb`
   */
  modelRelativePath: string
  /**
   * 相对 `src/assets/models/` 的缩略图；留空字符串则左侧卡片用 CSS 占位。
   * @example `Phone/iphone16.png`
   */
  thumbRelativePath: string
  /**
   * 屏幕对象名称；留空则 Three 视口使用默认屏幕对象。
   * @example `screen`
   */
  screenObjName: string | null
}

/**
 * 当前仓库内 `src/assets/models` 下的机型清单（可按需增删）。
 */
export const MOCKUP_MODEL_LIBRARY: readonly MockupModelLibraryEntry[] = [
  {
    id: 'iphone15-pro',
    category: 'phone',
    name: 'iphone15 Pro',
    width: 1179,
    height: 2556,
    modelRelativePath: 'Phone/iphone15-pro-compressed.glb',
    thumbRelativePath: 'Phone/iphone15-pro.png',
    screenObjName: 'screen'
  },
  {
    id: 'iphone15-promax',
    category: 'phone',
    name: 'iphone15 Pro Max',
    width: 1290,
    height: 2796,
    modelRelativePath: 'Phone/iphone15-promax-compressed.glb',
    thumbRelativePath: 'Phone/iphone15-promax.png',
    screenObjName: 'screen'
  },
  {
    id: 'iphone16',
    category: 'phone',
    name: 'iPhone 16',
    width: 1179,
    height: 2556,
    modelRelativePath: 'Phone/iphone16-compressed.glb',
    thumbRelativePath: 'Phone/iphone16.png',
    screenObjName: 'screen'
  },
  {
    id: 'iphone17',
    category: 'phone',
    name: 'iPhone 17',
    width: 1179,
    height: 2556,
    modelRelativePath: 'Phone/iphone17-compressed.glb',
    thumbRelativePath: 'Phone/iphone17.png',
    screenObjName: 'screen'
  },
  {
    id: 'iphoneAir',
    category: 'phone',
    name: 'iPhone Air',
    width: 1179,
    height: 2556,
    modelRelativePath: 'Phone/iphoneAir-compressed.glb',
    thumbRelativePath: 'Phone/iphoneAir.png',
    screenObjName: 'screen'
  },
  {
    id: 'ipadpro2020',
    category: 'tablet',
    name: 'iPad Pro (2020)',
    width: 1668,
    height: 2388,
    modelRelativePath: 'Tablet/ipadpro2020-compressed.glb',
    thumbRelativePath: 'Tablet/ipadpro2020.png',
    screenObjName: 'screen'
  },

  // ✅ 修正 MacBook Air 13
  {
    id: 'macbook13-air',
    category: 'laptop',
    name: 'MacBook13 Air',
    width: 2560,
    height: 1664,
    modelRelativePath: 'Computer/macbook13-air-compressed.glb',
    thumbRelativePath: 'Computer/macbook13-air.png',
    screenObjName: 'screen'
  },

  // ✅ 修正 MacBook Air 15
  {
    id: 'macbook15-air',
    category: 'laptop',
    name: 'MacBook15 Air',
    width: 2880,
    height: 1864,
    modelRelativePath: 'Computer/macbook15-air-compressed.glb',
    thumbRelativePath: 'Computer/macbook15-air.png',
    screenObjName: 'screen'
  },

  // ✅ MacBook Pro 16 原本就是对的
  {
    id: 'macbook16-prom3',
    category: 'laptop',
    name: 'MacBook16 Pro M3',
    width: 3456,
    height: 2234,
    modelRelativePath: 'Computer/macbook16-prom3-compressed.glb',
    thumbRelativePath: 'Computer/macbook16-prom3.png',
    screenObjName: 'screen'
  },

  {
    id: 'applewatchs7',
    category: 'watch',
    name: 'Apple Watch Series 7',
    width: 352,
    height: 430,
    modelRelativePath: 'Watch/applewatchs7-compressed.glb',
    thumbRelativePath: 'Watch/applewatchs7.png',
    screenObjName: 'screen'
  },
  {
    id: 'applewatchs10',
    category: 'watch',
    name: 'Apple Watch Series 10',
    width: 416,
    height: 496,
    modelRelativePath: 'Watch/applewatchs10-compressed.glb',
    thumbRelativePath: 'Watch/applewatchs10.png',
    screenObjName: 'screen'
  },
  {
    id: 'image-plane',
    category: 'other',
    name: '图片 / 视频',
    width: 1920,
    height: 1080,
    modelRelativePath: '',
    thumbRelativePath: 'Other/Video.png',
    screenObjName: null,
  },
  {
    id: 'text-plane',
    category: 'other',
    name: '文字',
    width: 1920,
    height: 240,
    modelRelativePath: '',
    thumbRelativePath: 'Other/Text.png',
    screenObjName: null,
  },
] as const
